import { useCallback, useMemo, useState } from "react";
import { CompositionService } from "../../../librettoEditor/services/CompositionService";
import { ScriptLayerStore } from "../../../librettoEditor/store/ScriptLayerStore";
import { ProjectAssetsStore } from "../../../state/ProjectAssetsStore";
import { trackSessionEvent } from "../../../analytics/sessionEvents";
import { applyRetakeDetections, detectRetakes, type RetakeDetection } from "../ai/retakes";

type GetTranscriptFn = (params: { transcriptId: string; projectId: string }) => Promise<any | undefined>;
type PostAiRetakesFn = (args: {
    projectId: string;
    maxDetections?: number;
    transcript: {
        text: string;
        tokens: Array<{
            tokenId: string;
            text: string;
            startSec: number;
            endSec: number;
            sourceIndex: number;
            speakerId?: string;
            speakerName?: string;
        }>;
    };
}) => Promise<
    | {
          detections?: Array<{
              id?: string;
              startTokenId?: string;
              endTokenId?: string;
              startSec?: number;
              endSec?: number;
              sourceIndex?: number;
              matchedText?: string;
              confidence?: number;
              reason?: string;
          }>;
      }
    | undefined
>;

export type RetakeReviewStatus = "pending" | "accepted" | "rejected";

type UseRetakesReviewWorkflowArgs = {
    getTranscript: GetTranscriptFn;
    postAiRetakes?: PostAiRetakesFn;
    onCloseSidebar: () => void;
};

const EPS = 1e-3;

const toFinite = (value: unknown, fallback: number): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
};

const detectionRangeIsValid = (d: RetakeDetection): boolean => {
    const startSec = toFinite(d?.startSec, NaN);
    const endSec = toFinite(d?.endSec, NaN);
    return Number.isFinite(startSec) && Number.isFinite(endSec) && endSec > startSec + EPS;
};

export const useRetakesReviewWorkflow = ({
    getTranscript,
    postAiRetakes,
    onCloseSidebar,
}: UseRetakesReviewWorkflowArgs) => {
    const [isDetecting, setIsDetecting] = useState(false);
    const [isApplyingAll, setIsApplyingAll] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasRun, setHasRun] = useState(false);
    const [draft, setDraft] = useState<RetakeDetection[]>([]);
    const [statusById, setStatusById] = useState<Record<string, RetakeReviewStatus>>({});
    const [busyById, setBusyById] = useState<Record<string, boolean>>({});
    const [appliedCutIdByDetectionId, setAppliedCutIdByDetectionId] = useState<Record<string, string>>({});
    const [activeDetectionId, setActiveDetectionId] = useState<string | null>(null);

    const draftById = useMemo(() => {
        const map = new Map<string, RetakeDetection>();
        for (const d of draft) {
            const id = String(d?.id ?? "").trim();
            if (!id) continue;
            map.set(id, d);
        }
        return map;
    }, [draft]);

    const pendingCount = useMemo(() => {
        return draft.reduce((count, d) => {
            const id = String(d?.id ?? "").trim();
            if (!id) return count;
            return statusById[id] === "pending" || !statusById[id] ? count + 1 : count;
        }, 0);
    }, [draft, statusById]);

    const acceptedCount = useMemo(() => {
        return draft.reduce((count, d) => {
            const id = String(d?.id ?? "").trim();
            if (!id) return count;
            return statusById[id] === "accepted" ? count + 1 : count;
        }, 0);
    }, [draft, statusById]);

    const rejectedCount = useMemo(() => {
        return draft.reduce((count, d) => {
            const id = String(d?.id ?? "").trim();
            if (!id) return count;
            return statusById[id] === "rejected" ? count + 1 : count;
        }, 0);
    }, [draft, statusById]);

    const clearReviewState = useCallback(() => {
        setError(null);
        setHasRun(false);
        setDraft([]);
        setStatusById({});
        setBusyById({});
        setAppliedCutIdByDetectionId({});
        setActiveDetectionId(null);
    }, []);

    const closeAndReset = useCallback(() => {
        clearReviewState();
        onCloseSidebar();
    }, [clearReviewState, onCloseSidebar]);

    const setBusy = useCallback((detectionId: string, busy: boolean) => {
        const id = String(detectionId ?? "").trim();
        if (!id) return;
        setBusyById((prev) => {
            const next = { ...prev };
            if (busy) next[id] = true;
            else delete next[id];
            return next;
        });
    }, []);

    const seekToDetection = useCallback((detectionId: string) => {
        const id = String(detectionId ?? "").trim();
        if (!id) return;
        const detection = draftById.get(id);
        if (!detection) return;

        const sourceStartSec = toFinite(detection.startSec, 0);
        const compTime = ScriptLayerStore.mapSourceTimeToCompositionTime(sourceStartSec);
        const seekTime = compTime != null && Number.isFinite(compTime) ? compTime : sourceStartSec;
        void CompositionService.seek(seekTime);
        setActiveDetectionId(id);
    }, [draftById]);

    const applySingleDetection = useCallback(async (detection: RetakeDetection): Promise<string | null> => {
        if (!detectionRangeIsValid(detection)) return null;
        const startSec = Number(detection.startSec);
        const endSec = Number(detection.endSec);
        const sourceIndex = toFinite(detection.sourceIndex, 0);

        try {
            const cut = await CompositionService.cutScriptLayerRangeWithAnalysis(startSec, endSec, sourceIndex, {
                transcriptMode: "retake-strikethrough",
            });
            const cutId = String((cut as any)?.id ?? "").trim();
            return cutId || null;
        } catch {
            const cut = CompositionService.cutScriptLayerRange(startSec, endSec, {
                transcriptMode: "retake-strikethrough",
            });
            const cutId = String((cut as any)?.id ?? "").trim();
            return cutId || null;
        }
    }, []);

    const acceptDetection = useCallback(async (detectionId: string) => {
        const id = String(detectionId ?? "").trim();
        if (!id || isApplyingAll || isDetecting || busyById[id]) return;
        const detection = draftById.get(id);
        if (!detection) return;
        if (statusById[id] === "accepted") {
            setActiveDetectionId(id);
            return;
        }

        setError(null);
        setBusy(id, true);
        try {
            trackSessionEvent("retake_removal");
            const cutId = await applySingleDetection(detection);

            setStatusById((prev) => ({ ...prev, [id]: "accepted" }));
            if (cutId) {
                setAppliedCutIdByDetectionId((prev) => ({ ...prev, [id]: cutId }));
            }
            setActiveDetectionId(id);
        } catch (e: any) {
            const msg = String(e?.message ?? e ?? "Failed to apply retake edit.");
            setError(msg);
        } finally {
            setBusy(id, false);
        }
    }, [applySingleDetection, busyById, draftById, isApplyingAll, isDetecting, setBusy, statusById]);

    const rejectDetection = useCallback(async (detectionId: string) => {
        const id = String(detectionId ?? "").trim();
        if (!id || isApplyingAll || isDetecting || busyById[id]) return;

        setError(null);
        setBusy(id, true);
        try {
            const existingCutId = String(appliedCutIdByDetectionId[id] ?? "").trim();
            if (existingCutId) {
                CompositionService.restoreScriptCut(existingCutId);
            }

            setAppliedCutIdByDetectionId((prev) => {
                const next = { ...prev };
                delete next[id];
                return next;
            });
            setStatusById((prev) => ({ ...prev, [id]: "rejected" }));
            setActiveDetectionId(id);
        } catch (e: any) {
            const msg = String(e?.message ?? e ?? "Failed to omit retake edit.");
            setError(msg);
        } finally {
            setBusy(id, false);
        }
    }, [appliedCutIdByDetectionId, busyById, isApplyingAll, isDetecting, setBusy]);

    const detect = useCallback(async () => {
        if (isDetecting || isApplyingAll) return;
        setIsDetecting(true);
        setError(null);

        try {
            if (!CompositionService.isInitialized()) {
                throw new Error("Composition is not ready yet.");
            }

            const projectId = String(ProjectAssetsStore.projectId ?? "").trim();
            if (!projectId) {
                throw new Error("Missing project id.");
            }

            const res = await detectRetakes({
                getTranscript,
                postAiRetakes,
                projectId,
                minConfidence: 0.9,
                maxGapSec: 10,
                maxCandidates: 40,
                useAudioVeto: true,
            });

            const detections = Array.isArray(res?.detections) ? res.detections : [];
            setDraft(detections);
            setStatusById({});
            setBusyById({});
            setAppliedCutIdByDetectionId({});
            setActiveDetectionId(detections[0]?.id ?? null);
        } catch (e: any) {
            const msg = String(e?.message ?? e ?? "Failed to detect retakes.");
            setError(msg);
        } finally {
            setHasRun(true);
            setIsDetecting(false);
        }
    }, [getTranscript, isApplyingAll, isDetecting, postAiRetakes]);

    const acceptAllRemaining = useCallback(async () => {
        if (isApplyingAll || isDetecting) return;

        const remaining = draft.filter((d) => {
            const id = String(d?.id ?? "").trim();
            if (!id) return false;
            const status = statusById[id] ?? "pending";
            return status === "pending";
        });

        if (!remaining.length) {
            closeAndReset();
            return;
        }

        setIsApplyingAll(true);
        setError(null);
        try {
            trackSessionEvent("retake_removal");
            await applyRetakeDetections({
                detections: remaining,
                transcriptMode: "retake-strikethrough",
            });
            closeAndReset();
        } catch (e: any) {
            const msg = String(e?.message ?? e ?? "Failed to apply retakes.");
            setError(msg);
        } finally {
            setIsApplyingAll(false);
        }
    }, [closeAndReset, draft, isApplyingAll, isDetecting, statusById]);

    return {
        isDetecting,
        isApplyingAll,
        isLoading: isDetecting || isApplyingAll,
        error,
        hasRun,
        draft,
        statusById,
        busyById,
        activeDetectionId,
        pendingCount,
        acceptedCount,
        rejectedCount,
        canAcceptAllRemaining: pendingCount > 0,
        detect,
        seekToDetection,
        acceptDetection,
        rejectDetection,
        acceptAllRemaining,
        reset: clearReviewState,
        done: closeAndReset,
        resetAndClose: closeAndReset,
    };
};

export type RetakesReviewWorkflow = ReturnType<typeof useRetakesReviewWorkflow>;
